import { DescriptionField } from "@dash/description-list";
import { Button, DateField } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { toDateString } from "@legacy-megarax/common";
import fileDownload from "js-file-download";
import { useEffect, useState } from "react";
import { MdOutlineDownload } from "react-icons/md";

import { UserWorkTimeReport } from "@megaron/crm-contracts";
import { useClientManager } from "@megaron/react-clients";

import { reportToCsv } from "./reportToCsv";
import { WorkTimeReportTable } from "./WorkTimeReportTable";

export type DateRange = [since: Date | null, until: Date | null];

export const WorkTimeReportDialog = () => {
  const { isMobile } = useDeviceType();

  const [reportData, setReportData] = useState<UserWorkTimeReport | null>(null);

  const [dateRange, setDateRange] = useState<DateRange>([null, null]);
  const isDateRangeSelected = dateRange[0] !== null && dateRange[1] !== null;

  const query = useClientManager("crm")
    .workTimeReportQuery()
    .useQuery(
      {
        legacyUserId: undefined,
        since: toDateString(dateRange[0] ?? new Date()),
        until: toDateString(dateRange[1] ?? new Date()),
      },
      {
        enabled: isDateRangeSelected,
      },
    );

  const report = query.data;

  useEffect(() => {
    if (report) {
      setReportData(report);
    }
  }, [report]);

  const download = () => {
    if (!report) return;
    const csv = reportToCsv(report);
    fileDownload(csv, `zestawienie_czasu_pracy.csv`);
  };

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        minWidth: isMobile ? "unset" : "20rem",
        maxHeight: "75dvh",
      }}
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <DateField onChange={(date) => setDateRange([date, dateRange[1]])} value={dateRange[0]} label="Od" />
        <DateField onChange={(date) => setDateRange([dateRange[0], date])} value={dateRange[1]} label="Do" />
      </div>
      {reportData && (
        <>
          <div css={{ display: "flex", alignItems: "center", ga: "1rem", justifyContent: "space-between" }}>
            <DescriptionField label="Razem">{formatWorkTime(reportData.totalWorkTimeMinutes)}</DescriptionField>
            <Button
              onClick={download}
              isDisabled={download === undefined || query.isLoading}
              icon={<MdOutlineDownload />}
            >
              Pobierz CSV
            </Button>
          </div>
          <WorkTimeReportTable report={reportData} isLoading={query.isLoading} />
        </>
      )}
    </div>
  );
};

export const formatWorkTime = (minutes: number) => {
  const fullHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes - fullHours * 60;

  const hourText = fullHours > 0 ? `${fullHours} godz.` : null;
  const minuteText = remainingMinutes > 0 ? `${remainingMinutes} min.` : null;

  if (hourText === null) return minuteText ?? "-";
  if (minuteText === null) return hourText;
  return `${hourText} ${minuteText}`;
};
